/**
 * AURA API Client
 *
 * HTTP client for communicating with the AURA backend gateway.
 * Handles authentication headers, request/response typing, and error handling.
 *
 * In production, this replaces the synthetic data generator.
 * The field can switch between synthetic (development) and live (production) data
 * based on the VITE_API_URL environment variable.
 */

// Re-declare essential types inline to avoid cross-package dependency.
// These mirror @aura/shared types — keep in sync.

interface Cluster {
  id: string; timestamp: number; status: string; confidence: number;
  topic: string; description?: string; entities: string[]; signalIds: string[];
  metadata: Record<string, any>; created_at: number; updated_at: number;
  scarring_score?: number;
}

interface Signal {
  id: string; type: string; timestamp: number; source: string;
  sourceId: string; content: string; entities: string[]; confidence: number;
  metadata: Record<string, any>; created_at: number; updated_at: number;
}

interface Activation {
  id: string; clusterId: string; timestamp: number; phase: string;
  intensity: number; source_signal_ids: string[]; affected_signal_ids: string[];
  metadata: Record<string, any>; created_at: number; updated_at: number;
}

interface TemporalState {
  clusterId: string; current_phase: string; activation_count: number;
  time_since_last_activation: number; decay_factor: number;
  projected_next_activation?: number; metadata: Record<string, any>;
}

interface CreateSignalRequest {
  type: string; source: string; sourceId: string; content: string;
  entities: string[]; confidence: number; metadata?: Record<string, any>;
}

interface CreateClusterRequest {
  topic: string; description?: string; entities: string[];
  signalIds: string[]; metadata?: Record<string, any>;
}

interface ListClustersRequest {
  status?: string; topic?: string; limit?: number; offset?: number;
}

interface ListClustersResponse {
  clusters: Cluster[]; total: number; limit: number; offset: number;
}

interface GetClusterResponse {
  cluster: Cluster; signals: Signal[]; activations: Activation[];
  related_clusters: Cluster[];
}

interface HealthCheckResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  services: Record<string, 'up' | 'down'>; timestamp: number;
}

// ============================================================================
// Configuration
// ============================================================================

// When VITE_API_URL is empty or 'self', use same-origin (Vercel serverless functions)
const rawApiUrl = import.meta.env.VITE_API_URL || '';
const API_URL = (rawApiUrl === 'self' || rawApiUrl === '') ? '' : rawApiUrl;

// ============================================================================
// Token Management
// ============================================================================

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

// ============================================================================
// HTTP Client
// ============================================================================

interface RequestOptions {
  method?: string;
  body?: any;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, signal } = options;

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (accessToken) {
    requestHeaders['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: requestHeaders,
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => 'Unknown error');
    const error = new Error(`API Error ${response.status}: ${errorBody}`);
    (error as any).status = response.status;
    (error as any).body = errorBody;
    throw error;
  }

  return response.json();
}

// ============================================================================
// Health
// ============================================================================

export async function healthCheck(): Promise<HealthCheckResponse> {
  return request<HealthCheckResponse>('/health');
}

// ============================================================================
// Clusters
// ============================================================================

export async function listClusters(params?: ListClustersRequest): Promise<ListClustersResponse> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.topic) query.set('topic', params.topic);
  if (params?.limit) query.set('limit', String(params.limit));
  if (params?.offset) query.set('offset', String(params.offset));

  const qs = query.toString();
  return request<ListClustersResponse>(`/api/clusters${qs ? '?' + qs : ''}`);
}

export async function getCluster(clusterId: string): Promise<GetClusterResponse> {
  return request<GetClusterResponse>(`/api/clusters/${clusterId}`);
}

export async function createCluster(data: CreateClusterRequest): Promise<Cluster> {
  return request<Cluster>('/api/clusters', { method: 'POST', body: data });
}

export async function updateClusterStatus(clusterId: string, status: string): Promise<Cluster> {
  return request<Cluster>(`/api/clusters/${clusterId}/status`, {
    method: 'PATCH',
    body: { status },
  });
}

// ============================================================================
// Signals
// ============================================================================

export async function createSignal(data: CreateSignalRequest): Promise<Signal> {
  return request<Signal>('/api/signals', { method: 'POST', body: data });
}

export async function getSignalsByCluster(clusterId: string): Promise<Signal[]> {
  return request<Signal[]>(`/api/clusters/${clusterId}/signals`);
}

// ============================================================================
// Activations
// ============================================================================

export async function triggerActivation(
  clusterId: string,
  signalIds: string[]
): Promise<Activation> {
  return request<Activation>('/api/activations', {
    method: 'POST',
    body: { clusterId, signalIds },
  });
}

export async function getActivations(clusterId: string): Promise<Activation[]> {
  return request<Activation[]>(`/api/clusters/${clusterId}/activations`);
}

// ============================================================================
// Temporal
// ============================================================================

export async function getTemporalState(clusterId: string): Promise<TemporalState> {
  return request<TemporalState>(`/api/temporal/${clusterId}`);
}

export async function getSnapshots(clusterId: string, limit?: number): Promise<any> {
  const query = limit ? `?limit=${limit}` : '';
  return request(`/api/temporal/${clusterId}/snapshots${query}`);
}

// ============================================================================
// Ingestion
// ============================================================================

export async function triggerIngestion(source: string, query: string): Promise<any> {
  return request('/api/ingestion/trigger', {
    method: 'POST',
    body: { source, query },
  });
}

export async function getIngestionStatus(): Promise<any> {
  return request('/api/ingestion/status');
}

// ============================================================================
// Field Data (combined endpoint for initial field load)
// ============================================================================

export interface FieldData {
  clusters: Cluster[];
  signals: Signal[];
  edges: Array<{ source: string; target: string; weight: number }>;
  temporalStates: Record<string, TemporalState>;
}

export async function getFieldData(): Promise<FieldData | null> {
  try {
    const data = await request<Partial<FieldData>>('/api/field');
    return {
      clusters: data?.clusters || [],
      signals: data?.signals || [],
      edges: data?.edges || [],
      temporalStates: data?.temporalStates || {},
    };
  } catch (err) {
    console.warn('[Field] getFieldData failed:', err);
    return null;
  }
}

// ============================================================================
// Data Source Toggle
// ============================================================================

/**
 * Feature flag — set to true when live data is ready to ship.
 * When false, both production and dev use synthetic data.
 * Flip this to `true` once we've verified the live data transformer
 * produces a field that matches the authored aesthetic.
 */
const LIVE_DATA_ENABLED = true;

/**
 * Returns true if the app should use live API data.
 * Gated by LIVE_DATA_ENABLED flag above.
 * In production builds (Vercel), uses live data via serverless /api/field.
 * In development, uses live data if VITE_API_URL is explicitly set.
 */
export function useLiveData(): boolean {
  if (!LIVE_DATA_ENABLED) return false;
  return import.meta.env.PROD || Boolean(import.meta.env.VITE_API_URL);
}
